import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { CLAIM_OUTCOME_LABELS } from '@/components/ClaimsTable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ClaimOutcome, ClaimStatus, type InternalClaim, useSetClaimStatus } from '@/models/claim'

/**
 * Owned by the page, not the row: closing a claim under a status filter drops its
 * row from the list, taking the inline outcome selector with it.
 */
export function ClaimOutcomeDialog({
  claim,
  open,
  onOpenChange,
}: {
  claim: InternalClaim
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const setStatus = useSetClaimStatus()
  const [outcome, setOutcome] = useState<ClaimOutcome | ''>(claim.outcome ?? '')

  useEffect(() => {
    if (open) setOutcome(claim.outcome ?? '')
  }, [open, claim.id, claim.outcome])

  function save() {
    if (!outcome) return
    setStatus.mutate(
      { id: claim.id, status: ClaimStatus.CLOSED, outcome },
      {
        onSuccess: () => {
          toast.success(`Closed as ${CLAIM_OUTCOME_LABELS[outcome].toLowerCase()}.`)
          onOpenChange(false)
        },
        onError: () => toast.error('That outcome did not save.'),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>How did {claim.idShort} end?</DialogTitle>
          <DialogDescription>
            The customer reads this on the closed step. Skip it and the claim stays closed without a reason
            until someone sets one.
          </DialogDescription>
        </DialogHeader>
        <Select
          value={outcome}
          disabled={setStatus.isPending}
          onValueChange={(next) => setOutcome(next as ClaimOutcome)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Pick an outcome" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(ClaimOutcome).map((value) => (
              <SelectItem key={value} value={value}>
                {CLAIM_OUTCOME_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Not yet
          </Button>
          <Button disabled={!outcome} loading={setStatus.isPending ? 'Saving…' : undefined} onClick={save}>
            Save outcome
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
